import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('privacy.title')}</h1>
        <p className="text-sm text-gray-500 mb-10">{t('privacy.lastUpdated')}</p>

        <div className="prose prose-gray max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <p className="text-gray-700 leading-relaxed">{t('privacy.intro')}</p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.collect.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacy.collect.desc')}</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.collect.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Camera & Device Permissions */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.permissions.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacy.permissions.desc')}</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.permissions.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Health & Wellbeing Data */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.health.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacy.health.desc')}</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.health.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">{t('privacy.health.note')}</p>
          </section>

          {/* Face Login — optional, consent-based (UU PDP: biometric data is
              "specific personal data"). Wording mirrors the consent text
              employees agree to in the app. */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.face.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacy.face.desc')}</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.face.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">{t('privacy.face.note')}</p>
          </section>

          {/* App usage analytics — customer app (lib/analytics.ts in
              ampera-mobile, app_events in Supabase). Keep in step with what
              the app actually records and the in-app opt-out wording. */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.analytics.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacy.analytics.desc')}</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.analytics.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">{t('privacy.analytics.note')}</p>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.use.title')}</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.use.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Data Storage & Security */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.storage.title')}</h2>
            <p className="text-gray-700">{t('privacy.storage.desc')}</p>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sharing.title')}</h2>
            <p className="text-gray-700">{t('privacy.sharing.desc')}</p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.rights.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacy.rights.desc')}</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              {(t('privacy.rights.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">
              {t('privacy.rights.note')}{' '}
              <Link to="/delete-account" className="text-primary-600 hover:underline">
                {t('privacy.rights.deleteLink')}
              </Link>
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.changes.title')}</h2>
            <p className="text-gray-700">{t('privacy.changes.desc')}</p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.contact.title')}</h2>
            <p className="text-gray-700">{t('privacy.contact.desc')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
